/* eslint-disable no-nested-ternary */
/* eslint-disable complexity */
/* eslint-disable max-lines */
import { css, styled, xStyledCommonProps } from '@elliemae/ds-system';
import { typographyFontFamily } from '@elliemae/ds-typography';
import { BUTTON_SHAPES, BUTTON_SIZES, BUTTON_TYPES, mobileSizes, sizes } from '../../constants.js';
import { DSButtonName, DSButtonSlots } from './DSButtonDefinitions.js';
import type { DSButtonT } from './react-desc-prop-types.js';

// PUI-18721 / PUI-18210 follow-up: only font-family is taken from the shared
// typography tokens here, scoped to V3 only (V2 never set font-family — applying
// it there would be a visible change). Per-size font-weight/line-height
// (b3/b1/h4-strong per the Button [Filled Variant] design spec v3.0.0) are
// intentionally deferred to a future iteration — this fix keeps font-weight,
// line-height, and font-size exactly as they were for every size.
const typographyBase = css<StyledButtonPropsT>`
  ${({ $isV3 }) => ($isV3 ? typographyFontFamily : '')}
`;

export interface StyledButtonPropsT {
  $size: DSButtonT.ButtonSizesT;
  $buttonType: DSButtonT.ButtonTypesT;
  $shape: DSButtonT.ButtonShapesT;
  $isV3: DSButtonT.RequiredProps['isV3'];
}

export const StyledPseudoBorder = styled('span')`
  display: none;
  position: absolute;
`;

const pseudoBorder = css<StyledButtonPropsT>`
  position: absolute;
  border-style: solid;
  border-width: ${({ $size, $buttonType }) =>
    $size === BUTTON_SIZES.S && $buttonType === BUTTON_TYPES.FILLED ? '1px' : '2px'};
  ${(styledProps) => {
    const { $buttonType, $shape, theme } = styledProps;
    if ($buttonType === BUTTON_TYPES.FILLED || $buttonType === BUTTON_TYPES.ICON_FILLED) {
      return `
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-color: ${theme.colors.neutral['000']};
          border-radius: ${$shape !== BUTTON_SHAPES.DEFAULT ? '50%' : '0px'};
      `;
    }
    return `
    top: -1px;
    left: -1px;
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    border-radius:  ${$shape !== BUTTON_SHAPES.DEFAULT ? '50%' : '2px'};
`;
  }}
`;

const fontSizeSettings = css<StyledButtonPropsT>`
  ${({ theme, $size, $buttonType }) => {
    switch ($size) {
      case BUTTON_SIZES.S:
        if ($buttonType === BUTTON_TYPES.TEXT) return `font-size: ${theme.fontSizes.label[200]};`;
        return `font-size: ${theme.fontSizes.label[300]};`;
      case BUTTON_SIZES.L:
        return `
        font-size: ${theme.fontSizes.value[600]};
    `;
      default:
        return `
        font-size: ${theme.fontSizes.value[500]};
    `;
    }
  }}
`;

const resetButtonStyles = css`
  outline: none;
  border: none;
  background: transparent;
  &:hover {
    background: transparent;
  }
`;

export const RawButton = styled('button', {
  name: DSButtonName,
  slot: DSButtonSlots.ROOT,
})`
  ${resetButtonStyles}
`;

const defaultButtonStyles = css<StyledButtonPropsT>`
  ${resetButtonStyles}
  // Apply the shared font-family (PUI-18721) as the text styling base for V3
  // buttons. font-weight (below), font-size (fontSizeSettings), and line-height
  // (below) are declared AFTER this and win the cascade unconditionally, so
  // button height/line-height/font-size/font-weight are all unaffected —
  // only font-family changes.
  ${typographyBase}
  display: inline-grid;
  grid-gap: 0.615rem;
  position: relative;
  grid-auto-flow: column;
  place-items: center;
  align-items: ${({ $buttonType, $isV3 }) =>
    $isV3 === false ||
    $buttonType === BUTTON_TYPES.ICON ||
    $buttonType === BUTTON_TYPES.ICON_OUTLINE ||
    $buttonType === BUTTON_TYPES.ICON_FILLED
      ? 'center'
      : 'start'};
  justify-items: center;
  width: fit-content;
  cursor: pointer;
  font-weight: 600;
  ${({ $isV3, theme }) => {
    if ($isV3) {
      return `
        align-content: space-evenly;
        text-align: left;
        line-height: 1rem;
          @media (min-width: ${theme.breakpoints.small}) {
            line-height: 1.23rem;
        }
      `;
    }
    return '';
  }}
  padding: ${({ theme, $buttonType, $size, $isV3 }) =>
    $buttonType === BUTTON_TYPES.ICON ||
    $buttonType === BUTTON_TYPES.ICON_OUTLINE ||
    $buttonType === BUTTON_TYPES.ICON_FILLED
      ? '0'
      : $isV3 === false
        ? `0 ${theme.space.xs};`
        : $size === BUTTON_SIZES.S
          ? `0.5px ${theme.space.xs} 0.5px`
          : $size === BUTTON_SIZES.M && ($buttonType === BUTTON_TYPES.OUTLINE || $buttonType === BUTTON_TYPES.TEXT)
            ? `2.5px ${theme.space.xs} 2.5px`
            : $size === BUTTON_SIZES.M
              ? `2px ${theme.space.xs} 2px`
              : $size === BUTTON_SIZES.L && ($buttonType === BUTTON_TYPES.OUTLINE || $buttonType === BUTTON_TYPES.TEXT)
                ? `5.5px ${theme.space.xs} 5.5px`
                : `5px ${theme.space.xs} 5px`};
  border-style: solid;
  ${({ $size, $isV3 }) => ($isV3 ? `min-height: ${mobileSizes[$size]};` : `height: ${mobileSizes[$size]};`)}
  min-width: ${({ $size }) => mobileSizes[$size]};
  @media (min-width: ${({ theme }) => theme.breakpoints.small}) {
    ${({ $size, $isV3 }) => ($isV3 ? `min-height: ${sizes[$size]};` : `height: ${sizes[$size]};`)}
    min-width: ${({ $size }) => sizes[$size]};
  }
  border-radius: ${(styledProps) => {
    const { $shape } = styledProps;
    return $shape === BUTTON_SHAPES.DEFAULT ? '2px' : '50%';
  }};
  ${fontSizeSettings}
  ${({ $buttonType }) => ($buttonType === BUTTON_TYPES.TEXT ? 'text-transform: uppercase;' : '')}
  &[aria-disabled='true'],
  &[disabled] {
    cursor: not-allowed;
  }
  ${xStyledCommonProps}
`;

export const DefaultButton = styled('button', {
  name: DSButtonName,
  slot: DSButtonSlots.ROOT,
})<StyledButtonPropsT>`
  ${defaultButtonStyles}
`;

export const FilledButton = styled('button', {
  name: DSButtonName,
  slot: DSButtonSlots.ROOT,
})<StyledButtonPropsT>`
  ${defaultButtonStyles}
  background-color: brand-600;
  border-color: brand-600;
  color: neutral-000;
  border-width: ${({ $size }) => ($size === 's' ? '1px;' : '2px')};
  &:focus,
  &[data-testemulatestate='focus'] {
    border-color: brand-700;
  }
  & svg {
    fill: neutral-000;
  }
  &:hover,
  &:active,
  &[data-testemulatestate='hover'],
  &[data-testemulatestate='active'] {
    background-color: brand-700;
    border-color: brand-700;
  }
  &[aria-disabled='true'],
  &[disabled],
  &:hover[aria-disabled='true'],
  &:hover[disabled],
  &:active[aria-disabled='true'],
  &:active[disabled],
  &[data-testemulatestate='hover'][aria-disabled='true'],
  &[data-testemulatestate='hover'][disabled],
  &[data-testemulatestate='active'][aria-disabled='true'],
  &[data-testemulatestate='active'][disabled] {
    background-color: neutral-100;
    border-color: neutral-100;
    color: #5c6574;
    & svg {
      fill: #5c6574;
    }
    &:focus {
      border-color: brand-700;
    }
  }
  &:focus .pseudo-border,
  &[data-testemulatestate='focus'] .pseudo-border {
    display: block;
    ${pseudoBorder}
  }

  /* Mostrar pseudo-border en focus + disabled */
  &[aria-disabled='true']:focus .pseudo-border,
  &[disabled]:focus .pseudo-border,
  &[aria-disabled='true'][data-testemulatestate='focus'] .pseudo-border,
  &[disabled][data-testemulatestate='focus'] .pseudo-border,
  &:hover[aria-disabled='true']:focus .pseudo-border,
  &:hover[disabled]:focus .pseudo-border,
  &:active[aria-disabled='true']:focus .pseudo-border,
  &:active[disabled]:focus .pseudo-border,
  &[data-testemulatestate='hover'][aria-disabled='true']:focus .pseudo-border,
  &[data-testemulatestate='hover'][disabled]:focus .pseudo-border,
  &[data-testemulatestate='active'][aria-disabled='true']:focus .pseudo-border,
  &[data-testemulatestate='acneutraltive'][disabled]:focus .pseudo-border {
    border-color: neutral-100;
  }
`;

export const OutlineButton = styled('button', {
  name: DSButtonName,
  slot: DSButtonSlots.ROOT,
})<StyledButtonPropsT>`
  ${defaultButtonStyles}
  background: neutral-000;
  border-color: neutral-400;
  color: brand-600;
  border-width: 1px;
  & svg {
    fill: brand-600;
  }
  &:hover,
  &:active,
  &[data-testemulatestate='hover'],
  &[data-testemulatestate='active'] {
    background-color: neutral-000;
    border-color: brand-700;
    color: brand-700;
    & svg {
      fill: brand-700;
    }
  }
  &[aria-disabled='true'],
  &[disabled],
  &:hover[aria-disabled='true'],
  &:hover[disabled],
  &:active[aria-disabled='true'],
  &:active[disabled],
  &[data-testemulatestate='hover'][aria-disabled='true'],
  &[data-testemulatestate='hover'][disabled],
  &[data-testemulatestate='active'][aria-disabled='true'],
  &[data-testemulatestate='active'][disabled] {
    border-color: neutral-400;
    color: neutral-500;
    & svg {
      fill: neutral-500;
    }
  }

  &:focus .pseudo-border,
  &[data-testemulatestate='focus'] .pseudo-border {
    display: block;
    ${pseudoBorder}
    border-color: brand-700;
  }
`;

export const TextButton = styled('button', {
  name: DSButtonName,
  slot: DSButtonSlots.ROOT,
})<StyledButtonPropsT>`
  ${defaultButtonStyles}
  border-color: transparent;
  background-color: transparent;
  border-width: 1px;
  color: brand-700;
  & svg {
    fill: brand-700;
  }
  &:hover:not([aria-disabled='true']),
  &:hover:not([disabled]),
  &[data-testemulatestate='hover'] {
    background-color: brand-200;
    color: brand-700;
    & svg {
      fill: brand-700;
    }
  }
  &:active,
  &[data-testemulatestate='active'] {
    color: brand-700;
  }

  &[aria-disabled='true'],
  &[disabled],
  &:hover[aria-disabled='true'],
  &:hover[disabled],
  &:active[aria-disabled='true'],
  &:active[disabled],
  &[data-testemulatestate='hover'][aria-disabled='true'],
  &[data-testemulatestate='hover'][disabled],
  &[data-testemulatestate='active'][aria-disabled='true'],
  &[data-testemulatestate='active'][disabled] {
    color: neutral-500;
    & svg {
      fill: neutral-500;
    }
  }

  &:focus .pseudo-border,
  &[data-testemulatestate='focus'] .pseudo-border {
    display: block;
    ${pseudoBorder}
    border-color: brand-700;
  }
`;
